import React, { useState, useRef, useEffect, useContext, createContext } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import { useProductSurface } from "./ProductSurfaceContext";

// Accessible dropdown Menu / Popover. The trigger element is passed as `button`; Menu wires
// aria-haspopup/aria-expanded onto it, opens a role=menu popover with roving arrow-key focus,
// Escape + Tab to close, click-outside to dismiss, and focus return to the trigger.
//
//   <Menu align="right" aria-label="Account" button={<Button variant="ghost">Account</Button>}>
//     <MenuItem icon={User} onSelect={goProfile}>Profile</MenuItem>
//     <MenuItem icon={LogOut} danger onSelect={signOut}>Sign out</MenuItem>
//   </Menu>

const MenuCtx = createContext({ close: () => {} });

export function Menu({ button, children, align = "left", className, portal = false, "aria-label": ariaLabel }) {
  const productSurface = useProductSurface();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const initialFocus = useRef("first");
  const idRef = useRef(null);
  if (!idRef.current) {
    idRef.current = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
  }
  const menuId = `menu-${idRef.current}`;
  const triggerId = button.props.id || `menu-trigger-${idRef.current}`;

  const [rect, setRect] = useState(null);   // portal mode: the trigger's viewport rect at open

  const close = () => {
    setOpen(false);
    // return focus to the trigger (function-component Button doesn't forward refs, so query it)
    wrapRef.current?.querySelector('button,[role="button"]')?.focus();
  };

  useEffect(() => {
    if (!open) return undefined;
    const items = Array.from(menuRef.current?.querySelectorAll(
      '[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"]'
    ) || []);
    (initialFocus.current === "last" ? items[items.length - 1] : items[0])?.focus();
    initialFocus.current = "first";
    const onDocDown = (e) => {
      if (!wrapRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDocDown);
    // portal mode: the panel is position:fixed and detached from the trigger's scroll context.
    // Instead of dismissing on the first scroll tick (hostile on long lists), re-anchor the panel
    // to the trigger's live rect (rAF-throttled) and dismiss only once the trigger itself leaves
    // the viewport — the behavior of every mature dropdown library.
    let raf = 0;
    const onScroll = portal ? () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const t = wrapRef.current?.querySelector('button,[role="button"]');
        const r = t?.getBoundingClientRect();
        if (!r || r.bottom < 0 || r.top > window.innerHeight || (r.width === 0 && r.height === 0)) setOpen(false);
        else setRect(r);
      });
    } : null;
    if (onScroll) {
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", onScroll);
    }
    return () => {
      document.removeEventListener("pointerdown", onDocDown);
      if (onScroll) {
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", onScroll);
        if (raf) cancelAnimationFrame(raf);
      }
    };
  }, [open, portal]);

  const onKeyDown = (e) => {
    const items = Array.from(menuRef.current?.querySelectorAll(
      '[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"]'
    ) || []);
    const idx = items.indexOf(document.activeElement);
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); items[(idx + 1) % items.length]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); items[(idx - 1 + items.length) % items.length]?.focus(); }
    else if (e.key === "Home") { e.preventDefault(); items[0]?.focus(); }
    else if (e.key === "End") { e.preventDefault(); items[items.length - 1]?.focus(); }
    else if (e.key === "Tab") { close(); }
    else if (e.key.length === 1 && !e.altKey && !e.ctrlKey && !e.metaKey) {
      const q = e.key.toLocaleLowerCase();
      const ring = [...items.slice(idx + 1), ...items.slice(0, idx + 1)];
      const hit = ring.find((it) => it.textContent?.trim().toLocaleLowerCase().startsWith(q));
      if (hit) { e.preventDefault(); hit.focus(); }
    }
  };

  const trigger = React.cloneElement(button, {
    id: triggerId,
    "aria-haspopup": "menu",
    "aria-expanded": open,
    "aria-controls": open ? menuId : undefined,
    onClick: (e) => {
      button.props.onClick?.(e);
      if (e.defaultPrevented) return;
      initialFocus.current = "first";
      if (portal) setRect(e.currentTarget.getBoundingClientRect());
      setOpen((o) => !o);
    },
    onKeyDown: (e) => {
      button.props.onKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (e.key === "ArrowDown") { e.preventDefault(); initialFocus.current = "first"; setOpen(true); }
      else if (e.key === "ArrowUp") { e.preventDefault(); initialFocus.current = "last"; setOpen(true); }
    },
  });

  const panelClasses = cn(
    "z-50 min-w-[11rem] max-w-[calc(100vw-1rem)]",
    "max-h-[min(20rem,calc(100dvh-4rem))] overflow-y-auto overscroll-contain",
    // 140ms scale-from-0.96 pop anchored at the trigger edge — never the upward page-section drift
    // (a menu below its trigger drifting UP reads directionally wrong; Linear-style origin pop).
    "origin-top rounded-xl border border-line bg-surface shadow-pop py-1 motion-safe:animate-menu-in motion-reduce:animate-none",
    className
  );
  const panel = open && (
    <MenuCtx.Provider value={{ close }}>
      <div
        id={menuId}
        ref={menuRef}
        data-ui="menu"
        data-product-surface={productSurface || undefined}
        role="menu"
        aria-label={ariaLabel || button.props["aria-label"]}
        aria-labelledby={ariaLabel || button.props["aria-label"] ? undefined : triggerId}
        onKeyDown={onKeyDown}
        {...(portal && rect ? {
          // fixed at the trigger's viewport rect — escapes any overflow container (clipping rule);
          // flips above the trigger when the viewport bottom is near.
          style: {
            position: "fixed",
            ...(rect.bottom + 260 > window.innerHeight
              ? { bottom: window.innerHeight - rect.top + 4 }
              : { top: rect.bottom + 4 }),
            ...(align === "right"
              ? { right: Math.max(8, window.innerWidth - rect.right) }
              : { left: rect.left }),
          },
        } : {})}
        className={cn(panelClasses, !portal && "absolute mt-1", !portal && (align === "right" ? "right-0" : "left-0"))}
      >
        {children}
      </div>
    </MenuCtx.Provider>
  );

  return (
    <div ref={wrapRef} className="relative inline-block">
      {trigger}
      {portal && open ? createPortal(panel, document.body) : panel}
    </div>
  );
}

export function MenuItem({
  children,
  onSelect,
  icon: Icon,
  danger,
  disabled,
  closeOnSelect = true,
  className,
  ...rest
}) {
  const { close } = useContext(MenuCtx);
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      {...rest}
      aria-disabled={disabled || undefined}
      onClick={(e) => {
        if (disabled) { e.preventDefault(); return; }
        if (closeOnSelect) close?.();
        onSelect?.(e);
      }}
      className={cn(
        "flex min-h-11 w-full items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors",
        "outline-none hover:bg-muted",
        "focus:bg-muted focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
        danger ? "text-critical" : "text-ink-2",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {Icon && <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function MenuSeparator() {
  return <div role="separator" className="my-1 border-t border-line" />;
}
